'use client'

// Supabase auth island (login-system R2): hydrates session state client-side after paint.
// Server components never read auth — pages stay static; anything personalized mounts under
// this provider and fetches once the session is known.
import React, { createContext, useContext, useEffect, useState } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { SUPABASE_CONFIGURED, supabaseBrowser } from '@/lib/supabase/client'
import { markReturningMember } from '@/lib/supabase/returningMember'

type SupabaseAuthState = {
  /** false until the first getSession() resolves — render skeletons, not logged-out flashes */
  isLoaded: boolean
  session: Session | null
  user: User | null
  signOut: () => Promise<void>
}

const SupabaseAuthContext = createContext<SupabaseAuthState>({
  isLoaded: !SUPABASE_CONFIGURED,
  session: null,
  user: null,
  signOut: async () => {},
})

export const useSupabaseAuth = () => useContext(SupabaseAuthContext)

// The browser client writes the session cookie with JavaScript, and Safari caps such
// cookies at 7 days (a member away for longer came back logged out). After each new token
// the server re-issues the same cookies with their full lifetime: see /api/auth/stamp.
// Once per token per tab; the marker survives reloads so a page view costs no extra call.
const STAMPED_KEY = 'ea_session_stamped'
function stampSessionCookies(session: Session) {
  const mark = session.access_token.slice(-24)
  try {
    if (window.sessionStorage.getItem(STAMPED_KEY) === mark) return
    window.sessionStorage.setItem(STAMPED_KEY, mark)
  } catch {
    // storage blocked: stamp anyway, an extra call beats a lost login
  }
  void fetch('/api/auth/stamp', { method: 'POST' }).catch(() => {})
}

export const SupabaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(!SUPABASE_CONFIGURED)
  const [session, setSession] = useState<Session | null>(null)

  useEffect(() => {
    if (!SUPABASE_CONFIGURED) return
    const supabase = supabaseBrowser()
    const seen = (s: Session | null) => {
      if (s) {
        markReturningMember()
        stampSessionCookies(s)
      }
      setSession(s)
      setIsLoaded(true)
    }
    supabase.auth
      .getSession()
      .then(({ data }) => seen(data.session))
      // never leave the page waiting: an unreadable session is a logged-out visitor
      .catch(() => setIsLoaded(true))
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => seen(s))
    return () => sub.subscription.unsubscribe()
  }, [])

  const signOut = async () => {
    if (!SUPABASE_CONFIGURED) return
    await supabaseBrowser().auth.signOut()
  }

  return (
    <SupabaseAuthContext.Provider
      value={{ isLoaded, session, user: session?.user ?? null, signOut }}
    >
      {children}
    </SupabaseAuthContext.Provider>
  )
}
